import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { catchError, map, Observable, tap, throwError } from 'rxjs'
import { environment } from '../../../environments/environment'
import { FilesResponse } from '../../models/files.interface'

@Injectable({
    providedIn: 'root',
})
export class UploadfileService {
    private apiUrl = environment.service_1 + 'upload/'
    private url = environment.service_1 + 'files/'
    private downloadUrl = environment.service_1 + 'file-metadata/'

    httpOptions = {
        headers: new HttpHeaders({}),
    }

    constructor(private http: HttpClient) {}

    uploadFile(file: File, incidentId?: number): Observable<any> {
        const formData = new FormData()
        formData.append('file', file)
        if (incidentId) {
            formData.append('incident_id', incidentId.toString())
        }
        return this.http.post(this.apiUrl, formData)
    }

    allFiles(): Observable<FilesResponse> {
        return this.http.get<FilesResponse>(this.url, {
            headers: this.httpOptions.headers,
        })
    }
    getDownloadUrl(id: string) {
        return `${this.downloadUrl}${encodeURIComponent(id)}/`
    }

    private downloadFile(
        fileId: string
    ): Observable<{ blob: Blob; fileName?: string }> {
        const url = this.getDownloadUrl(fileId)
        return this.http
            .get(url, { observe: 'response', responseType: 'blob' })
            .pipe(
                map((res) => {
                    const cd = res.headers.get('Content-Disposition') || ''
                    const fileName = this.getNameFromCD(cd) || undefined
                    const pdfBlob = new Blob([res.body!], {
                        type: 'application/pdf',
                    })
                    return { blob: pdfBlob, fileName }
                })
            )
    }

    //ABRIR PDF EN UNA NUEVA PESTAÑA
    openPdfById(
        fileId: string,
        fallbackName = 'archivo.pdf'
    ): Observable<void> {
        if (!fileId) return throwError(() => new Error('fileId vacío'))
        // ABRIR VENTANA
        const win = window.open('', '_blank')

        return this.downloadFile(fileId).pipe(
            tap(({ blob, fileName }) => {
                const name = this.extensionPdf(
                    (fileName || fallbackName).trim()
                )
                const url = URL.createObjectURL(blob)
                this.openPdfUrlInNewTab(win, url, name)
            }),
            // CERRAR VENTANA SI HAY ERROR
            catchError((err) => {
                if (win) win.close()
                throw err
            }),
            map(() => void 0)
        )
    }

    //VISUALIZAR PDF EN EL FORMULARIO
    previewPdfUrl(fileId: string): Observable<string> {
        if (!fileId) return throwError(() => new Error('fileId vacío'))
        return this.downloadFile(fileId).pipe(
            map(({ blob }) => URL.createObjectURL(blob))
        )
    }

    //TRAER ARCHIVO
    fetchPdf(fileId: string, filename = 'archivo.pdf'): Observable<void> {
        if (!fileId) return throwError(() => new Error('fileId vacío'))
        return this.downloadFile(fileId).pipe(
            tap(({ blob, fileName }) => {
                const name = this.extensionPdf((fileName || filename).trim())
                const url = URL.createObjectURL(blob)
                this.triggerDownload(url, name)
                URL.revokeObjectURL(url)
            }),
            map(() => void 0)
        )
    }

    // VISUALIZAR ARCHIVO LOCAL
    openLocalPdf(file: File, fallbackName = 'archivo.pdf'): void {
        if (!file) return
        const name = this.extensionPdf(file.name || fallbackName)
        const blob =
            file.type === 'application/pdf'
                ? file
                : new Blob([file], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const win = window.open('', '_blank')
        this.openPdfUrlInNewTab(win, url, name)
    }

    //REVOCAR ARCHIVO URL
    revokeUrl(url?: string) {
        if (url) URL.revokeObjectURL(url)
    }

    private getNameFromCD(cd: string): string | null {
        const m = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd)
        return m ? decodeURIComponent(m[1].replace(/\"/g, '')) : null
    }

    private extensionPdf(name: string): string {
        return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`
    }

    private openPdfUrlInNewTab(
        win: Window | null,
        pdfUrl: string,
        title: string
    ) {
        if (!win) {
            const w = window.open(pdfUrl, '_blank')
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 120_000)
            return
        }
        win.document.open()
        win.document.write(`
      <!doctype html>
      <html>
        <head><meta charset="utf-8"><title></title></head>
        <body style="margin:0">
          <iframe src="${pdfUrl}#zoom=page-width"
                  style="border:0;width:100vw;height:100vh"></iframe>
        </body>
      </html>
    `)
        win.document.close()
        win.document.title = title
        win.addEventListener('beforeunload', () => URL.revokeObjectURL(pdfUrl))
    }

    private triggerDownload(url: string, filename: string) {
        const a = document.createElement('a')
        a.href = url
        a.download = filename || 'archivo.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
    }
}
